import _ from 'lodash'
import React, { useContext } from 'react'
import { navigate, preventDefaultAndProp } from '../../utils/marketIdPathFunctions'
import { useHistory } from 'react-router'
import { Menu, MenuItem, Sidebar as ProSidebar, SubMenu } from 'react-pro-sidebar'
import { IconButton, Tooltip } from '@material-ui/core';
import Link from '@material-ui/core/Link';
import { ExpandLess, ExpandMore } from '@material-ui/icons'
import { useIntl } from 'react-intl'
import { getPageReducerPage, usePageStateReducer } from '../PageState/pageStateHooks'
import { PLANNING_TYPE } from '../../constants/markets'
import { DARK_ACTION_BUTTON_COLOR, useButtonColors } from '../Buttons/ButtonConstants'
import AddIcon from '@material-ui/icons/Add'
import NotificationCountChips from '../../pages/Dialog/NotificationCountChips'
import { ThemeModeContext } from '../../contexts/ThemeModeContext'
import { SearchResultsContext } from '../../contexts/SearchResultsContext/SearchResultsContext'

function processRegularItem(properties) {
  const {history, text, target, num, Icon, iconColor='black', onClickFunc, isBold, isBlue, complexIcon,
    index, openMenuItems, isSubMenu, onEnterFunc, onLeaveFunc, endIcon: EndIcon, linkHref, resetFunction, tipText, idPrepend='',
    numSuffix='', hasCritical, onChipClick, chipTooltipId, sidebarColor, sidebarSelectedColor, isDark, navigateFromLeftNav} = properties;
  if (!text) {
    return React.Fragment
  }
  const textNoSpaces = text.split(' ').join('');
  if (!target && !onClickFunc) {
    if (tipText) {
      return (
        <Tooltip key={`tip${textNoSpaces}`}
                 title={tipText}>
          <span style={{paddingLeft: '3.25rem'}}>{text}</span>
        </Tooltip>
      );
    }
    if (linkHref) {
      return (
        <span style={{paddingLeft: '1.5rem', fontWeight: 'bold'}}>
          <Link href={linkHref} target="_blank">{text}</Link>
        </span>
      );
    }
    return (
      <span style={{paddingLeft: '3.25rem'}}>{text}</span>
    );
  }
  const key = `${index}${textNoSpaces}`;
  const isLink = isBold && !isBlue;
  const isActive = isBold && !isSubMenu && isBlue;
  // Selected item: a clean elevated pill (white in light, lighter slate in
  // dark) that reads clearly against the deeper teal sidebar (C-all-981).
  const backgroundColor = isActive ? sidebarSelectedColor : undefined;
  const boxShadow = isActive ? '0 1px 3px rgba(16, 40, 40, 0.22)' : undefined;
  const textInner = isBold ? (<span
      style={{fontWeight: 'bold', color: isLink ? '#2F80ED' : undefined}}>{text}</span>)
    : <span>{text}</span>;
  // The sidebar truncates labels (view/group names can be far longer than fits), so show the full
  // name on hover - below the item, like the avatar does for a user name - when it's long enough to clip.
  const textRepresentation = text.length > 20 ? (
    <Tooltip key={`tipFull${textNoSpaces}`} title={text}>{textInner}</Tooltip>
  ) : textInner;
  const useIdPrepend =  complexIcon ? idPrepend + index : idPrepend;

  return (
    <div key={`sidebarMenuHolder${key}`}>
      <MenuItem icon={complexIcon ? Icon : <Icon htmlColor={iconColor} style={{fontSize: '1rem', marginBottom: '0.15rem'}} />}
                style={{backgroundColor, boxShadow, borderRadius: 22}}
                rootStyles={{
                  '.css-wx7wi4': {
                    marginRight: 0,
                  },
                }}
                key={key} id={`${useIdPrepend}${textNoSpaces}`}
                suffix={num > 0 ?
                 <NotificationCountChips num={num} numSuffix={numSuffix} hasCritical={hasCritical}
                   onChipClick={onChipClick} chipTooltipId={chipTooltipId} />
                  : (EndIcon ? <IconButton id={`end${useIdPrepend}${textNoSpaces}`} size="small" 
                    style={{transform: 'translateX(5px)'}}
                  onClick={(event) => onClickFunc(event)}>
                      <EndIcon htmlColor={isDark ? DARK_ACTION_BUTTON_COLOR : 'black'} fontSize="small" /></IconButton>
                    : undefined)}
                onClick={
                  (event) => {
                    if (EndIcon) {
                      if (resetFunction) {
                        resetFunction(event);
                      }
                    } else {
                      if (onClickFunc) {
                        onClickFunc(event)
                      } else {
                        navigateFromLeftNav(() => navigate(history, target, false))
                      }
                    }
                  }
                }
                onMouseEnter={
                  (event) => {
                    if (onEnterFunc) {
                      onEnterFunc(event);
                    }
                  }
                }
                onMouseLeave={
                  (event) => {
                    if (onLeaveFunc) {
                      onLeaveFunc(event);
                    }
                  }
                }
      >
        {textRepresentation}
      </MenuItem>
      {!_.isEmpty(openMenuItems) && (
        <div style={{paddingLeft: '1rem', backgroundColor: sidebarColor}} key="openMenuItems">
          {openMenuItems.map((subItem, index) => {
            const { text, target, num, numSuffix, hasCritical, onChipClick, chipTooltipId, icon: Icon, onClickFunc, isBold } = subItem
            return processRegularItem({history, text, target, num, numSuffix, hasCritical, onChipClick, chipTooltipId, Icon, onClickFunc,
              isBold, index, isSubMenu: true, sidebarColor, sidebarSelectedColor, isDark,
              navigateFromLeftNav})
          })}
        </div>
      )}
    </div>
  );
}

export default function Sidebar(props) {
  const history = useHistory();
  const intl = useIntl();
  const [themeMode] = useContext(ThemeModeContext);
  const isDark = themeMode === 'dark';
  const [searchResults] = useContext(SearchResultsContext);
  const isSearch = !_.isEmpty(searchResults?.search);
  const { marketId, navigationOptions, idPrepend='', navigateFromLeftNav } = props;
  const { actionButtonColor, sidebarColor, sidebarSelectedColor } = useButtonColors();
  // Single deeper teal for the whole sidebar panel so it reads as distinct
  // from the main area; sub-views use the same color (no banding). Colors are
  // named constants in ButtonConstants (SIDEBAR_COLOR etc.) for easy tuning
  // (T-all-2173).
  const [pageStateFull, pageDispatch] = usePageStateReducer('sidebarMenus');
  const [pageState, updatePageState] = getPageReducerPage(pageStateFull, pageDispatch, marketId || 'sidebarState',
    {viewsOpen: true});
  // While searching always reveal the overflow groups so a match below the first five isn't hidden.
  const viewsOpen = pageState.viewsOpen || isSearch;
  const { navListItemTextArray, navLowerListItemTextArray, navMenu, navLowerMenu, listOnClick } = navigationOptions || {};
  const firstFiveNavListItemTextArray = navListItemTextArray?.slice(0, 5);
  const moreFiveNavListItemTextArray = navListItemTextArray?.slice(5);
  return (
    <ProSidebar width="16rem" backgroundColor={sidebarColor} style={{borderRightWidth: '0px'}}>
        {navMenu}
        {!_.isEmpty(navMenu) && (
          <Menu rootStyles={{
            '.ps-menu-button': {paddingLeft: '16px', height: '30px', overflow: 'hidden'},
            '.ps-menu-button:hover': {backgroundColor: isDark ? 'black' : undefined}
          }}
            renderExpandIcon={() => <div onClick={(event)=> {
              preventDefaultAndProp(event);
              if (marketId) {
                navigateFromLeftNav(() =>
                  navigate(history, `/wizard#type=${PLANNING_TYPE.toLowerCase()}&marketId=${marketId}`));
              }
            }}><Tooltip placement='top' title={intl.formatMessage({ id: 'homeAddGroup' })}>
            <IconButton size="small" id="addViewId">
            <AddIcon htmlColor={marketId ? actionButtonColor : 'disabled'} fontSize="small" />
            </IconButton>
          </Tooltip></div>}>
            <SubMenu id='views'
                    label={intl.formatMessage({ id: 'viewInGroup' })}
                    rootStyles={{
                      '.ps-menuitem-root': {
                        backgroundColor: sidebarColor
                      }
                    }}
                      key="views" open >
                      {firstFiveNavListItemTextArray?.map((navItem, topIndex) => {
                        const { text, target, num, numSuffix, hasCritical, onChipClick, chipTooltipId, icon: Icon, complexIcon, onClickFunc, isBold, isBlue, openMenuItems,
                          onEnterFunc, onLeaveFunc, endIcon, resetFunction, tipText, linkHref, iconColor } = navItem;
                        return processRegularItem({history, text, target, num, numSuffix, hasCritical, onChipClick, chipTooltipId, Icon, complexIcon, onClickFunc, isBold,
                          isBlue, linkHref, index: topIndex, openMenuItems, onEnterFunc, onLeaveFunc, endIcon, resetFunction,
                          tipText, idPrepend, sidebarColor, sidebarSelectedColor, iconColor, isDark,
                          navigateFromLeftNav})
                      })}
                      {navListItemTextArray.length > 5 && (
                        <MenuItem
                          icon={viewsOpen ? <ExpandLess htmlColor={isDark ? DARK_ACTION_BUTTON_COLOR : 'black'} style={{fontSize: '1rem', marginBottom: '0.15rem'}} /> 
                          : <ExpandMore htmlColor={isDark ? DARK_ACTION_BUTTON_COLOR : 'black'} style={{fontSize: '1rem', marginBottom: '0.15rem'}} />}
                          id="moreViewsId"
                          key="moreViewsKey"
                          onClick={(event) => {
                            preventDefaultAndProp(event);
                            updatePageState({viewsOpen: !viewsOpen});
                          }}
                        >
                          {intl.formatMessage({ id: viewsOpen ? 'less' : 'more' })}
                        </MenuItem>
                      )}
                      {viewsOpen && moreFiveNavListItemTextArray?.map((navItem, topIndex) => {
                        const { text, target, num, numSuffix, hasCritical, onChipClick, chipTooltipId, icon: Icon, complexIcon, onClickFunc, isBold, isBlue, openMenuItems,
                          onEnterFunc, onLeaveFunc, endIcon, resetFunction, tipText, linkHref, iconColor } = navItem;
                        return processRegularItem({history, text, target, num, numSuffix, hasCritical, onChipClick, chipTooltipId, Icon, complexIcon, onClickFunc, isBold,
                          isBlue, linkHref, index: topIndex, openMenuItems, onEnterFunc, onLeaveFunc, endIcon, resetFunction,
                          tipText, idPrepend, sidebarColor, sidebarSelectedColor, iconColor, isDark,
                          navigateFromLeftNav})
                      })}
              </SubMenu>
          </Menu>
        )}
        <div style={{marginBottom: '1rem'}} />
        {!_.isEmpty(navLowerListItemTextArray) && (
          <Menu 
            rootStyles={{
              '.ps-menu-button': {height: 'unset', paddingLeft: '10px'},
              '.ps-menu-button:hover': {backgroundColor: isDark ? 'black' : undefined}
            }}
            onClick={listOnClick}>
            {navLowerListItemTextArray.map((navItem, topIndex) => {
              const { text, target, num, icon: Icon, complexIcon, onClickFunc, isBold, isBlue, openMenuItems,
                onEnterFunc, onLeaveFunc, endIcon, resetFunction, tipText, linkHref, iconColor } = navItem;
              return processRegularItem({history, text, target, num, Icon, complexIcon, onClickFunc, isBold,
                isBlue, linkHref, index: topIndex, openMenuItems, onEnterFunc, onLeaveFunc, endIcon, resetFunction,
                tipText, idPrepend, sidebarColor, sidebarSelectedColor, iconColor, isDark,
                navigateFromLeftNav})
            })}
          </Menu>
        )}
        <div style={{marginBottom: '0.5rem'}} />
        {navLowerMenu}
    </ProSidebar>
  );
}
